import { useId, useMemo, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { buildTicks, vizColor } from '~/lib/viz';

export interface LineSeries {
  key: string;
  label: string;
  color: string;
}

interface LineChartProps {
  labels: string[];
  series: LineSeries[];
  values: Record<string, (number | null)[]>;
  height?: number;
  formatValue: (v: number) => string;
  formatTick: (v: number) => string;
  areaFill?: boolean;
  referenceIndex?: number;
  referenceLabel?: string;
}

const VIEW_W = 640;
const PAD_L = 52;
const PAD_R = 12;
const PAD_T = 16;
const PAD_B = 28;

function buildPath(xs: number[], ys: (number | null)[]): string {
  const segments: string[] = [];
  let open = false;
  xs.forEach((x, i) => {
    const y = ys[i];
    if (y == null) {
      open = false;
      return;
    }
    segments.push(`${open ? 'L' : 'M'} ${x} ${y}`);
    open = true;
  });
  return segments.join(' ');
}

export function LineChart({
  labels,
  series,
  values,
  height = 220,
  formatValue,
  formatTick,
  areaFill = false,
  referenceIndex,
  referenceLabel,
}: LineChartProps) {
  const clipId = useId();
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);

  const plotW = VIEW_W - PAD_L - PAD_R;
  const plotH = height - PAD_T - PAD_B;

  const maxValue = useMemo(() => {
    let max = 0;
    for (const key of Object.keys(values)) {
      for (const v of values[key]) {
        if (v != null && v > max) max = v;
      }
    }
    return max;
  }, [values]);

  const ticks = useMemo(() => buildTicks(maxValue, 4), [maxValue]);
  const top = ticks[ticks.length - 1] || 1;

  const xs = labels.map((_, i) => PAD_L + (labels.length > 1 ? (i / (labels.length - 1)) * plotW : plotW / 2));
  const yFor = (v: number) => PAD_T + plotH - (v / top) * plotH;

  const handlePointerMove = (e: ReactPointerEvent<SVGRectElement>) => {
    const rect = e.currentTarget.ownerSVGElement?.getBoundingClientRect();
    if (!rect) return;
    const relX = ((e.clientX - rect.left) / rect.width) * VIEW_W;
    const stepX = labels.length > 1 ? plotW / (labels.length - 1) : plotW;
    let idx = Math.round((relX - PAD_L) / stepX);
    idx = Math.max(0, Math.min(labels.length - 1, idx));
    setHoverIndex(idx);
  };

  const hoverX = hoverIndex != null ? xs[hoverIndex] : null;
  const showLegend = series.length >= 2;

  return (
    <div className="relative">
      {showLegend ? (
        <div className="mb-2 flex flex-wrap gap-x-4 gap-y-1">
          {series.map((s) => (
            <div key={s.key} className="flex items-center gap-1.5 text-xs text-[color:var(--text-secondary)]">
              <span className="inline-block h-[2px] w-3 rounded" style={{ backgroundColor: s.color }} />
              {s.label}
            </div>
          ))}
        </div>
      ) : null}
      <svg
        viewBox={`0 0 ${VIEW_W} ${height}`}
        className="w-full"
        style={{ height, overflow: 'visible' }}
        onPointerLeave={() => setHoverIndex(null)}
      >
        <defs>
          <clipPath id={clipId}>
            <rect x={PAD_L} y={PAD_T} width={plotW} height={plotH} />
          </clipPath>
        </defs>

        {ticks.map((t) => (
          <g key={t}>
            <line
              x1={PAD_L}
              x2={VIEW_W - PAD_R}
              y1={yFor(t)}
              y2={yFor(t)}
              stroke={vizColor.grid}
              strokeWidth={1}
            />
            <text x={PAD_L - 8} y={yFor(t)} textAnchor="end" dominantBaseline="middle" fontSize={10} fill={vizColor.textMuted}>
              {formatTick(t)}
            </text>
          </g>
        ))}
        <line x1={PAD_L} x2={PAD_L} y1={PAD_T} y2={PAD_T + plotH} stroke={vizColor.axis} strokeWidth={1} />

        {labels.map((l, i) =>
          i % Math.ceil(labels.length / 9) === 0 ? (
            <text key={l + i} x={xs[i]} y={height - 8} textAnchor="middle" fontSize={10} fill={vizColor.textMuted}>
              {l}
            </text>
          ) : null,
        )}

        {referenceIndex != null ? (
          <g>
            <line
              x1={xs[referenceIndex]}
              x2={xs[referenceIndex]}
              y1={PAD_T}
              y2={PAD_T + plotH}
              stroke={vizColor.axis}
              strokeWidth={1}
              strokeDasharray="3 3"
            />
            {referenceLabel ? (
              <text x={xs[referenceIndex] + 4} y={PAD_T + 10} fontSize={10} fill={vizColor.textMuted}>
                {referenceLabel}
              </text>
            ) : null}
          </g>
        ) : null}

        <g clipPath={`url(#${clipId})`}>
          {series.map((s) => {
            const ys = values[s.key].map((v) => (v == null ? null : yFor(v)));
            const path = buildPath(xs, ys);
            return (
              <g key={s.key}>
                {areaFill && series.length === 1 ? (
                  <path
                    d={`${path} L ${xs[xs.length - 1]} ${PAD_T + plotH} L ${xs[0]} ${PAD_T + plotH} Z`}
                    fill={s.color}
                    opacity={0.1}
                    stroke="none"
                  />
                ) : null}
                <path d={path} fill="none" stroke={s.color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
              </g>
            );
          })}
        </g>

        {hoverX != null ? (
          <line x1={hoverX} x2={hoverX} y1={PAD_T} y2={PAD_T + plotH} stroke={vizColor.axis} strokeWidth={1} />
        ) : null}
        {hoverIndex != null
          ? series.map((s) => {
              const v = values[s.key][hoverIndex];
              if (v == null) return null;
              return (
                <circle
                  key={s.key}
                  cx={xs[hoverIndex]}
                  cy={yFor(v)}
                  r={4}
                  fill={s.color}
                  stroke={vizColor.surface}
                  strokeWidth={2}
                />
              );
            })
          : null}

        <rect
          x={PAD_L}
          y={PAD_T}
          width={plotW}
          height={plotH}
          fill="transparent"
          onPointerMove={handlePointerMove}
          onPointerLeave={() => setHoverIndex(null)}
        />
      </svg>

      {hoverIndex != null ? (
        <div
          className="pointer-events-none absolute top-0 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs shadow-md"
          style={{
            left: `${(xs[hoverIndex] / VIEW_W) * 100}%`,
            transform: xs[hoverIndex] / VIEW_W > 0.7 ? 'translateX(-100%)' : 'translateX(8px)',
          }}
        >
          <div className="mb-1 font-medium text-slate-900">{labels[hoverIndex]}</div>
          {series.map((s) => {
            const v = values[s.key][hoverIndex];
            return (
              <div key={s.key} className="flex items-center gap-2 text-slate-600">
                <span className="inline-block h-[2px] w-2.5 rounded" style={{ backgroundColor: s.color }} />
                <span className="text-slate-500">{s.label}:</span>
                <span className="font-semibold text-slate-900">{v == null ? '—' : formatValue(v)}</span>
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
